import React, { useState } from 'react';
import './LeadCaptureForm.css';

const LeadCaptureForm = () => {
    // Form state
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        // Company Information
        company_name: '',
        domain: '',
        company_size: '',
        industry: 'Finance/Banking',
        
        // Primary Contact
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        
        // Regulatory Requirements
        regulatory_requirements: [],
        custom_regulatory_requirement: '',
        
        // Discovery Information
        pain_points: [],
        estimated_budget: '',
        marketing_source: ''
    });
    
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Available options
    const companySizeOptions = [
        { value: '10M-50M', label: '$10M - $50M' },
        { value: '50M-100M', label: '$50M - $100M' },
        { value: '100M-250M', label: '$100M - $250M' },
        { value: '250M-500M', label: '$250M - $500M' },
        { value: '500M+', label: '$500M+' }
    ];
    
    const regulatoryOptions = [
        'SOX',
        'GLBA',
        'PCI-DSS',
        'HIPAA',
        'CCPA',
        'GDPR',
        'FERPA',
        'Other'
    ];
    
    const painPointOptions = [
        'Lack of quantitative risk data',
        'Compliance preparation/audits',
        'Security program maturity assessment',
        'Third-party vendor risk',
        'Cloud security posture',
        'Incident response planning'
    ];
    
    const marketingSourceOptions = [
        'LinkedIn',
        'Google Search',
        'Referral',
        'Conference/Event',
        'Industry Publication',
        'Other'
    ];
    
    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };
    
    // Handle checkbox changes
    const handleCheckboxChange = (field, value) => {
        setFormData(prev => {
            const currentValues = prev[field];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            
            return {
                ...prev,
                [field]: newValues
            };
        });
    };
    
    // Validation functions
    const validateStep1 = () => {
        const newErrors = {};
        
        if (!formData.company_name.trim()) {
            newErrors.company_name = 'Company name is required';
        }
        
        if (!formData.company_size) {
            newErrors.company_size = 'Company size is required';
        }
        
        if (!formData.contact_name.trim()) {
            newErrors.contact_name = 'Contact name is required';
        }
        
        if (!formData.contact_email.trim()) {
            newErrors.contact_email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.contact_email)) {
            newErrors.contact_email = 'Email is invalid';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const validateStep2 = () => {
        const newErrors = {};
        
        if (formData.regulatory_requirements.length === 0) {
            newErrors.regulatory_requirements = 'Please select at least one regulatory requirement';
        }
        
        if (formData.pain_points.length === 0) {
            newErrors.pain_points = 'Please select at least one pain point';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    // Navigation functions
    const nextStep = () => {
        let isValid = true;
        
        if (currentStep === 1) {
            isValid = validateStep1();
        } else if (currentStep === 2) {
            isValid = validateStep2();
        }
        
        if (isValid && currentStep < 3) {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        }
    };
    
    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        }
    };
    
    // Submit to tier selection (doesn't actually submit to backend yet)
    const handleSubmit = async () => {
        if (!validateStep2()) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Store form data in sessionStorage to pass to tier selection
            sessionStorage.setItem('leadFormData', JSON.stringify(formData));
            
            // Redirect to tier selection page
            window.location.href = '/tier-selection';
            
        } catch (error) {
            console.error('Error:', error);
            setErrors({ submit: 'An error occurred. Please try again.' });
            setIsSubmitting(false);
        }
    };
    
    // Render step content
    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return renderStep1();
            case 2:
                return renderStep2();
            default:
                return renderStep1();
        }
    };
    
    // Step 1: Company and Contact Information
    const renderStep1 = () => (
        <>
            <h3>Company Information</h3>
            
            <div className="form-group">
                <label htmlFor="company_name" className="required">Company Name</label>
                <input
                    type="text"
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    required
                />
                {errors.company_name && <div className="error">{errors.company_name}</div>}
            </div>
            
            <div className="form-group">
                <label htmlFor="domain">Company Website</label>
                <input
                    type="text"
                    id="domain"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    placeholder="example.com"
                />
            </div>
            
            <div className="form-group">
                <label htmlFor="company_size" className="required">Annual Revenue</label>
                <select
                    id="company_size"
                    name="company_size"
                    value={formData.company_size}
                    onChange={handleInputChange}
                    required
                >
                    <option value="">Select revenue range...</option>
                    {companySizeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {errors.company_size && <div className="error">{errors.company_size}</div>}
            </div>
            
            <div className="form-group">
                <label htmlFor="industry">Industry</label>
                <input
                    type="text"
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                />
            </div>
            
            <h3 style={{ marginTop: '2rem' }}>Primary Contact</h3>
            
            <div className="form-group">
                <label htmlFor="contact_name" className="required">Contact Name</label>
                <input
                    type="text"
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    required
                />
                {errors.contact_name && <div className="error">{errors.contact_name}</div>}
            </div>
            
            <div className="form-group">
                <label htmlFor="contact_email" className="required">Email</label>
                <input
                    type="email"
                    id="contact_email"
                    name="contact_email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    required
                />
                {errors.contact_email && <div className="error">{errors.contact_email}</div>}
            </div>
            
            <div className="form-group">
                <label htmlFor="contact_phone">Phone Number</label>
                <input
                    type="tel"
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleInputChange}
                    placeholder="555-1212 or (555) 555-1212"
                />
            </div>
            
            <div className="button-group">
                <button type="button" className="btn btn-success" onClick={nextStep}>
                    Continue
                </button>
            </div>
        </>
    );
    
    // Step 2: Regulatory Requirements and Pain Points
    const renderStep2 = () => (
        <>
            <h3>Regulatory Requirements</h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
                Which regulations does your organization need to comply with? (Select all that apply)
            </p>
            
            <div className="checkbox-group">
                {regulatoryOptions.map(option => (
                    <div key={option} className="checkbox-item">
                        <input
                            type="checkbox"
                            id={`reg_${option}`}
                            checked={formData.regulatory_requirements.includes(option)}
                            onChange={() => handleCheckboxChange('regulatory_requirements', option)}
                        />
                        <label htmlFor={`reg_${option}`}>{option}</label>
                    </div>
                ))}
            </div>
            {errors.regulatory_requirements && <div className="error">{errors.regulatory_requirements}</div>}
            
            {formData.regulatory_requirements.includes('Other') && (
                <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label htmlFor="custom_regulatory_requirement">Please specify other requirements</label>
                    <input
                        type="text"
                        id="custom_regulatory_requirement"
                        name="custom_regulatory_requirement"
                        value={formData.custom_regulatory_requirement}
                        onChange={handleInputChange}
                        placeholder="State banking regulations, etc."
                    />
                </div>
            )}
            
            <h3 style={{ marginTop: '2rem' }}>Current Security Challenges</h3>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
                What are your primary security pain points? (Select all that apply)
            </p>
            
            <div className="checkbox-group">
                {painPointOptions.map(option => (
                    <div key={option} className="checkbox-item">
                        <input
                            type="checkbox"
                            id={`pain_${option}`}
                            checked={formData.pain_points.includes(option)}
                            onChange={() => handleCheckboxChange('pain_points', option)}
                        />
                        <label htmlFor={`pain_${option}`}>{option}</label>
                    </div>
                ))}
            </div>
            {errors.pain_points && <div className="error">{errors.pain_points}</div>}
            
            <h3 style={{ marginTop: '2rem' }}>Additional Information</h3>
            
            <div className="form-group">
                <label htmlFor="estimated_budget">Estimated Security Budget</label>
                <select
                    id="estimated_budget"
                    name="estimated_budget"
                    value={formData.estimated_budget}
                    onChange={handleInputChange}
                >
                    <option value="">Prefer not to say</option>
                    <option value="Low">Low (&lt;$50K/year)</option>
                    <option value="Medium">Medium ($50K-$150K/year)</option>
                    <option value="High">High ($150K+/year)</option>
                </select>
            </div>
            
            <div className="form-group">
                <label htmlFor="marketing_source">How did you hear about us?</label>
                <select
                    id="marketing_source"
                    name="marketing_source"
                    value={formData.marketing_source}
                    onChange={handleInputChange}
                >
                    <option value="">Select one...</option>
                    {marketingSourceOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
            </div>
            
            <div className="button-group">
                <button type="button" className="btn btn-secondary" onClick={prevStep}>
                    Back
                </button>
                <button 
                    type="button" 
                    className="btn btn-success" 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <span className="loading"></span> : null}
                    Continue to Pricing
                </button>
            </div>
        </>
    );
    
    return (
        <div className="lead-capture-page">
            <div className="form-container">
                <h2>Get Started with CISO Advisor</h2>
                
                {/* Progress indicator */}
                <div className="progress-steps">
                    <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                        <div className="progress-step-number">1</div>
                        <div className="progress-step-label">Company Info</div>
                    </div>
                    <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                        <div className="progress-step-number">2</div>
                        <div className="progress-step-label">Requirements</div>
                    </div>
                    <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
                        <div className="progress-step-number">3</div>
                        <div className="progress-step-label">Select Tier</div>
                    </div>
                </div>
                
                {errors.submit && <div className="error">{errors.submit}</div>}
                
                <form>
                    {renderStep()}
                </form>
            </div>
        </div>
    );
};

export default LeadCaptureForm;
